import {
  useEffect,
  type ReactNode,
  createContext,
  useContext,
  useMemo,
  useCallback,
  useState,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router';

export const SKIP_PAGE_REF = 'skipPageRef';
const defaultFallback = '/my/';

type HistoryContextType = {
  history: readonly string[];
  previousPage: string | undefined;
  goToPreviousPage: (fallback?: string) => void;
};

const HistoryContext = createContext<HistoryContextType>({
  history: [],
  previousPage: undefined,
  goToPreviousPage: () => {},
});
HistoryContext.displayName = 'HistoryContext';

export function useHistoryContext() {
  const context = useContext(HistoryContext);
  if (!context) {
    throw new Error('Missing <HistoryContext.Provider />');
  }
  return context;
}

/**
 * Returns the current pathname including query and hash strings
 */
const useFullPathname = () => {
  const { pathname, search, hash } = useLocation();
  return `${pathname}${search}${hash}`;
};

const useNavigationTracking = () => {
  const location = useLocation();
  useEffect(() => {
    window.analytics?.page('Page Viewed', {});
  }, [location]);
};
/**
 * Provides a history of visited routes.
 *
 * Note: This is not the same a the browser history where POP operations are performed.
 */
export function HistoryProvider({
  children,
}: {
  children: ReactNode | ReactNode[];
}) {
  const [history, setHistory] = useState<string[]>([]);
  const { state } = useLocation();
  const pathname = useFullPathname();
  const navigate = useNavigate();

  // track navigation
  useNavigationTracking();

  const previousPage =
    history.length - 2 >= 0 ? history[history.length - 2] : undefined;

  // Latest data for stale closures
  const previousPageRef = useRef(previousPage);
  previousPageRef.current = previousPage;

  // Maintains a list of internal routes ordered by last visited
  useEffect(() => {
    if (state?.action === 'goToPreviousPage') {
      setHistory((prev) => prev.slice(0, prev.length - 1));
      return;
    } else if (
      state?.action === SKIP_PAGE_REF ||
      history[history.length - 1] === pathname
    ) {
      // Forced override OR if the current path and last path in history are the same, we want to prevent a navigation loop.
      return;
    }
    setHistory((prev) => [...prev, pathname]);
  }, [pathname]);

  /**
   * Go to the last viewed page.
   */
  const goToPreviousPage = useCallback((fallback = defaultFallback) => {
    const previousPath = previousPageRef.current ?? fallback;
    navigate(previousPath, {
      state: {
        action: 'goToPreviousPage',
      },
    });
  }, []);

  const value = useMemo(
    () => ({
      history,
      previousPage,
      goToPreviousPage,
    }),
    [history, previousPage, goToPreviousPage],
  );

  return (
    <HistoryContext.Provider value={value}>{children}</HistoryContext.Provider>
  );
}
